import { motion, useReducedMotion } from "framer-motion";
import { MessageCircle, ChevronDown, Wrench } from "lucide-react";
import { HeroSceneSafe } from "../three/HeroScene";
import { waLink, WA_DEFAULT_MSG, IMAGES } from "../lib/site";

const MaskedLine = ({ children, delay = 0, className = "" }) => {
  const reduce = useReducedMotion();
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={reduce ? { opacity: 0 } : { y: "110%" }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
};

const HudChip = ({ label, value, className = "", testId, delay = 0 }) => (
  <motion.div
    data-testid={testId}
    initial={{ opacity: 0, scale: 0.85 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay, duration: 0.6 }}
    className={`absolute float-slow bg-[#101218]/80 backdrop-blur-md border border-white/10 rounded-xl px-3.5 py-2.5 ${className}`}
  >
    <div className="font-mono text-[10px] tracking-widest text-slate-500 uppercase">{label}</div>
    <div className="font-mono text-sm font-semibold text-red-400" dir="ltr">{value}</div>
  </motion.div>
);

export const HeroSection = () => {
  const reduce = useReducedMotion();
  return (
    <section
      data-testid="hero-section"
      className="relative hero-radial overflow-hidden pt-24 sm:pt-28 pb-10 min-h-[100svh] flex items-center"
    >
      <div className="absolute inset-0 tech-grid-bg opacity-60" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-2 gap-10 items-center">
        <div className="order-2 lg:order-1">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="inline-flex items-center gap-2 text-xs font-mono tracking-widest text-slate-400 border border-white/10 bg-white/5 rounded-full px-4 py-1.5 mb-6"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
            الرياض — متخصصو أنظمة مراقبة ضغط الإطارات TPMS
          </motion.span>

          <h1
            data-testid="hero-title"
            className="font-display font-extrabold tracking-tight leading-[1.15] text-4xl sm:text-5xl lg:text-6xl text-white"
          >
            <MaskedLine delay={0.25}>حساسات كفرات الرياض</MaskedLine>
            <MaskedLine delay={0.45} className="text-red-500 text-2xl sm:text-3xl lg:text-4xl mt-3 font-bold">
              تقنية أدق لقياس ضغط الكفرات
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl"
          >
            حساسات ضغط الكفرات TPMS، وبرمجة الحساسات، وفحصها واستبدالها، مع
            مساعدتك في اختيار الحساس المتوافق مع سيارتك.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <a
              href={waLink(WA_DEFAULT_MSG)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="inline-flex items-center gap-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-7 py-3.5 rounded-full shadow-[0_4px_24px_rgba(16,185,129,0.4)] hover:shadow-[0_6px_32px_rgba(16,185,129,0.55)] transition-all active:scale-[0.97]"
            >
              <MessageCircle size={19} />
              استفسر عبر واتساب
            </a>
            <a
              href="#services"
              data-testid="hero-explore-btn"
              className="inline-flex items-center gap-2.5 bg-white/5 hover:bg-white/10 text-white border border-white/15 hover:border-red-500/40 font-semibold px-7 py-3.5 rounded-full backdrop-blur-md transition-all active:scale-[0.97]"
            >
              <Wrench size={18} className="text-red-500" />
              اكتشف خدماتنا
            </a>
          </motion.div>
        </div>

        <div className="order-1 lg:order-2 relative h-[340px] sm:h-[440px] lg:h-[560px]">
          <div data-testid="hero-canvas-3d" className="absolute inset-0">
            <HeroSceneSafe
              reduced={reduce}
              fallbackImage={IMAGES.heroRim}
              fallbackAlt="جنط رياضي فاخر مع فرامل حمراء وكفر عالي الأداء"
            />
          </div>
          <HudChip testId="hero-psi-stat-card" label="Tire Pressure" value="32.4 PSI" className="top-6 right-2 sm:right-6" delay={1.1} />
          <HudChip testId="hero-rf-stat-card" label="RF Signal" value="433 MHz" className="bottom-10 left-2 sm:left-8" delay={1.3} />
          <HudChip testId="hero-temp-stat-card" label="Sensor Temp" value="28°C" className="bottom-24 right-4 sm:right-14 hidden sm:block" delay={1.5} />
        </div>
      </div>

      <motion.a
        href="#tech"
        aria-label="انتقل للأسفل"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-5 left-1/2 -translate-x-1/2 text-slate-500 hover:text-red-400 transition-colors"
      >
        <ChevronDown className="animate-bounce" size={22} />
      </motion.a>
    </section>
  );
};
